import { AxiosResponse } from "axios";
import { useMemo, CSSProperties, useState, useRef, useEffect } from "react";
import {
  LayoutStyle,
  DisplayFlags,
  ServerListResponse,
  Bundle,
} from "../../types";
import { sizeEstimator } from "../cards/hooks/useGridCardDimensions";
import { ResultsGrid } from "./ResultsGrid";

interface BundleResultsGridProps {
  gridHeightAdjustment: CSSProperties["height"];
  layout: { style: LayoutStyle; display: DisplayFlags };
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  hasNextPage: boolean;
  allPages: AxiosResponse<ServerListResponse<Bundle>>[];
  columnPadding?: number;
  onScroll?: (scrollableRef: HTMLDivElement) => void;
  header?: React.ReactNode;
  gridKey: string;
}

export function BundleResultsGrid({
  gridHeightAdjustment,
  layout,
  isLoading,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  allPages,
  columnPadding = 16,
  onScroll,
  header,
  gridKey,
}: BundleResultsGridProps) {
  const [gridWidth, setGridWidth] = useState<number | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  // Resize observer effect for responsive grid width
  useEffect(() => {
    const gridEl = gridContainerRef.current;
    if (!gridEl) return;

    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      // Use immediate update for initial load, timeout for subsequent resizes
      const updateWidth = () => {
        const width = gridEl.getBoundingClientRect().width;
        if (width > 0) {
          setGridWidth(width);
        }
      };
      
      if (gridWidth === null) {
        updateWidth();
      } else {
        timeoutId = setTimeout(updateWidth, 100);
      }
    };
    
    // Initial measurement
    handleResize();

    window.addEventListener("resize", handleResize);

    // Cleanup: remove the event listener when the component unmounts
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, [gridWidth]);

  const dimensions = useMemo(() => {
    return sizeEstimator(
      layout.style.context,
      layout.style.mode,
      layout.style.size,
    );
  }, [layout]);

  // Bundle-specific column count calculation
  const columnCount = useMemo(() => {
    if (gridWidth === null) return 1; // Default to 1 column while loading
    const minCardWidth = dimensions.width;
    return Math.max(1, Math.floor(gridWidth / (minCardWidth + columnPadding)));
  }, [gridWidth, dimensions.width, columnPadding]);

  // Don't render grid content until width is measured to prevent full-width flash
  const measuredPages = gridWidth === null ? [] : allPages;

  return (
    <ResultsGrid
      key={`${gridKey}-${gridWidth}`}
      header={header}
      gridHeightAdjustment={gridHeightAdjustment ?? "325px"}
      layout={layout}
      isLoading={isLoading || gridWidth === null}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      allPages={measuredPages as AxiosResponse<ServerListResponse<Bundle>>[]}
      onScroll={onScroll}
      gridContainerRef={gridContainerRef}
      columnCountOverride={columnCount}
      gridItemsAlign="flex-start"
      gridItemWidth="100%"
      gridItemHeight="auto"
    />
  );
}
